import { Outlet, Link, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Layout({ user, onLogout }) {
  const location = useLocation();
  const [companySettings, setCompanySettings] = useState(null);
  const [showPurchaseMenu, setShowPurchaseMenu] = useState(false);
  const [showJobWorkMenu, setShowJobWorkMenu] = useState(false);
  const [showMastersMenu, setShowMastersMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobilePurchaseOpen, setMobilePurchaseOpen] = useState(false);
  const [mobileJobWorkOpen, setMobileJobWorkOpen] = useState(false);
  const [mobileMastersOpen, setMobileMastersOpen] = useState(false);
  const purchaseRef = useRef(null);
  const mastersRef = useRef(null);
  const purchaseTimeoutRef = useRef(null);
  const mastersTimeoutRef = useRef(null);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (purchaseRef.current && !purchaseRef.current.contains(event.target)) {
        setShowPurchaseMenu(false);
        setShowJobWorkMenu(false);
      }
      if (mastersRef.current && !mastersRef.current.contains(event.target)) {
        setShowMastersMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    return location.pathname === path ? 'bg-red-700' : 'hover:bg-red-700';
  };

  const isPurchaseActive = () => {
    return location.pathname.startsWith('/jobwork') || 
           location.pathname.includes('material') ||
           location.pathname.includes('vendor-performance') ||
           location.pathname.includes('quality-inspection');
  };

  const isMastersActive = () => {
    return location.pathname === '/parts-master' || location.pathname === '/customers';
  };

  // Purchase menu handlers
  const handlePurchaseMouseEnter = () => {
    if (purchaseTimeoutRef.current) clearTimeout(purchaseTimeoutRef.current);
    setShowPurchaseMenu(true);
  };

  const handlePurchaseMouseLeave = () => {
    purchaseTimeoutRef.current = setTimeout(() => {
      setShowPurchaseMenu(false);
      setShowJobWorkMenu(false);
    }, 150);
  };

  // Masters menu handlers
  const handleMastersMouseEnter = () => {
    if (mastersTimeoutRef.current) clearTimeout(mastersTimeoutRef.current);
    setShowMastersMenu(true);
  };

  const handleMastersMouseLeave = () => {
    mastersTimeoutRef.current = setTimeout(() => {
      setShowMastersMenu(false);
    }, 150);
  };

  const handleMenuClick = () => {
    setShowPurchaseMenu(false);
    setShowJobWorkMenu(false);
    setShowMastersMenu(false);
    setMobileMenuOpen(false);
  };

  const primaryColor = companySettings?.primary_color || '#dc2626';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-red-600 text-white shadow-lg" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4 md:space-x-8">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-red-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
              
              <div className="flex items-center space-x-2 md:space-x-3">
                <img 
                  src="/assets/company-logo.png" 
                  alt="Axis Industries"
                  className="h-8 md:h-10 w-auto bg-white px-1 py-0.5 rounded"
                />
                <div>
                  <h1 className="text-sm md:text-base lg:text-lg font-bold leading-tight">{companySettings?.company_name || 'Axis Industries'}</h1>
                  <p className="text-xs text-red-100 hidden lg:block">Manufacturing ERP</p>
                </div>
              </div>
              <div className="hidden lg:flex space-x-1">
                <Link
                  to="/"
                  className={`px-4 py-2 rounded-lg transition ${isActive('/')}`}
                  data-testid="nav-dashboard"
                >
                  Dashboard
                </Link>
                <Link
                  to="/schedules"
                  className={`px-4 py-2 rounded-lg transition ${isActive('/schedules')}`}
                  data-testid="nav-schedules"
                >
                  Schedules
                </Link>
                <Link
                  to="/dispatches"
                  className={`px-4 py-2 rounded-lg transition ${isActive('/dispatches')}`}
                  data-testid="nav-dispatches"
                >
                  Dispatches
                </Link>
                <Link
                  to="/production"
                  className={`px-4 py-2 rounded-lg transition ${isActive('/production')}`}
                  data-testid="nav-production"
                >
                  Production
                </Link>
                
                {/* Purchase Module Dropdown */}
                <div 
                  className="relative"
                  ref={purchaseRef}
                  onMouseEnter={handlePurchaseMouseEnter}
                  onMouseLeave={handlePurchaseMouseLeave}
                >
                  <button
                    className={`px-4 py-2 rounded-lg transition ${isPurchaseActive() ? 'bg-red-700' : 'hover:bg-red-700'}`}
                    data-testid="nav-purchase"
                    onClick={() => setShowPurchaseMenu(!showPurchaseMenu)}
                  >
                    Purchase ▾
                  </button>
                  {showPurchaseMenu && (
                    <div 
                      className="absolute top-full left-0 bg-white rounded-lg shadow-xl py-2 min-w-[220px] z-[100]"
                      onMouseEnter={handlePurchaseMouseEnter}
                      onMouseLeave={handlePurchaseMouseLeave}
                    >
                      {/* Job Work Sub-module */}
                      <div 
                        className="relative"
                        onMouseEnter={() => setShowJobWorkMenu(true)}
                        onMouseLeave={() => setShowJobWorkMenu(false)}
                      >
                        <div className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 cursor-pointer transition">
                          <span>🔧 Job Work</span>
                          <span className="text-xs">▶</span>
                        </div>
                        {showJobWorkMenu && (
                          <div className="absolute left-full top-0 bg-white rounded-lg shadow-xl py-2 min-w-[200px] ml-1">
                            <Link
                              to="/jobwork-dashboard"
                              className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                              onClick={handleMenuClick}
                            >
                              📊 Dashboard
                            </Link>
                            <Link
                              to="/jobwork-vendors"
                              className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                              onClick={handleMenuClick}
                            >
                              🏭 Vendors & Items
                            </Link>
                            <Link
                              to="/jobwork-pos"
                              className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                              onClick={handleMenuClick}
                            >
                              📋 Purchase Orders
                            </Link>
                            <Link
                              to="/jobwork-challans"
                              className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                              onClick={handleMenuClick}
                            >
                              📝 Challans (Daily)
                            </Link>
                            <Link
                              to="/quality-inspection"
                              className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                              onClick={handleMenuClick}
                            >
                              🔍 Quality Inspection
                            </Link>
                            <Link
                              to="/send-material"
                              className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                              onClick={handleMenuClick}
                            >
                              📤 Send Material
                            </Link>
                            <Link
                              to="/receive-material"
                              className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                              onClick={handleMenuClick}
                            >
                              📥 Receive Material
                            </Link>
                            <Link
                              to="/vendor-performance"
                              className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                              onClick={handleMenuClick}
                            >
                              📈 Vendor Performance
                            </Link>
                            <Link
                              to="/jobwork-balance"
                              className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                              onClick={handleMenuClick}
                            >
                              ⚖️ Job Work Balance
                            </Link>
                          </div>
                        )}
                      </div>
                      
                      {/* Future sub-modules placeholder */}
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <div className="px-4 py-2 text-xs text-gray-400 uppercase">Coming Soon</div>
                        <div className="px-4 py-2 text-gray-400 text-sm">📥 Direct Purchase</div>
                        <div className="px-4 py-2 text-gray-400 text-sm">🛒 Consumables</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Masters Dropdown */}
                <div 
                  className="relative"
                  ref={mastersRef}
                  onMouseEnter={handleMastersMouseEnter}
                  onMouseLeave={handleMastersMouseLeave}
                >
                  <button
                    className={`px-4 py-2 rounded-lg transition ${isMastersActive() ? 'bg-red-700' : 'hover:bg-red-700'}`}
                    data-testid="nav-masters"
                    onClick={() => setShowMastersMenu(!showMastersMenu)}
                  >
                    Masters ▾
                  </button>
                  {showMastersMenu && (
                    <div 
                      className="absolute top-full left-0 bg-white rounded-lg shadow-xl py-2 min-w-[200px] z-[100]"
                      onMouseEnter={handleMastersMouseEnter}
                      onMouseLeave={handleMastersMouseLeave}
                    >
                      <Link
                        to="/parts-master"
                        className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                        onClick={handleMenuClick}
                      >
                        📦 Parts Master
                      </Link>
                      <Link
                        to="/customers"
                        className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 transition"
                        onClick={handleMenuClick}
                      >
                        👥 Customers
                      </Link>
                      {/* Future masters placeholder */}
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <div className="px-4 py-2 text-xs text-gray-400 uppercase">Coming Soon</div>
                        <div className="px-4 py-2 text-gray-400 text-sm">⚙️ Machines</div>
                        <div className="px-4 py-2 text-gray-400 text-sm">🏢 Warehouses</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Admin-only navigation items */}
                {user && user.role === 'admin' && (
                  <>
                    <Link
                      to="/settings"
                      className={`px-4 py-2 rounded-lg transition ${isActive('/settings')}`}
                      data-testid="nav-settings"
                    >
                      Settings
                    </Link>
                    <Link
                      to="/users"
                      className={`px-4 py-2 rounded-lg transition ${isActive('/users')}`}
                      data-testid="nav-users"
                    >
                      Users
                    </Link>
                  </>
                )}

                {/* Help Center - Available to all users */}
                <Link
                  to="/help"
                  className={`px-4 py-2 rounded-lg transition ${isActive('/help')}`}
                  data-testid="nav-help"
                >
                  📖 Help
                </Link>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-right hidden sm:block">
                <div className="font-semibold">{user?.full_name || 'User'}</div>
                <div className="text-red-100 text-xs capitalize">{user?.role || 'user'}</div>
              </div>
              <button
                onClick={onLogout}
                className="bg-red-700 hover:bg-red-800 px-3 py-2 rounded-lg transition text-sm"
                data-testid="logout-button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-red-700 border-t border-red-500">
            <div className="px-4 py-3 space-y-2">
              <Link to="/" onClick={handleMenuClick} className="block px-3 py-2 rounded-lg hover:bg-red-600">
                Dashboard
              </Link>
              
              {/* Mobile Purchase Menu */}
              <div>
                <button 
                  onClick={() => setMobilePurchaseOpen(!mobilePurchaseOpen)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-600 flex justify-between items-center"
                >
                  Purchase
                  <span>{mobilePurchaseOpen ? '▼' : '▶'}</span>
                </button>
                {mobilePurchaseOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    <button 
                      onClick={() => setMobileJobWorkOpen(!mobileJobWorkOpen)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-600 flex justify-between items-center text-sm"
                    >
                      Job Work
                      <span>{mobileJobWorkOpen ? '▼' : '▶'}</span>
                    </button>
                    {mobileJobWorkOpen && (
                      <div className="ml-4 space-y-1 text-sm">
                        <Link to="/jobwork-dashboard" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600">Dashboard</Link>
                        <Link to="/jobwork-pos" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600">Purchase Orders</Link>
                        <Link to="/jobwork-challans" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600">Challans (Daily)</Link>
                        <Link to="/jobwork-vendors" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600">Vendors</Link>
                        <Link to="/jobwork-balance" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600">Balance</Link>
                      </div>
                    )}
                    <Link to="/send-material" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600 text-sm">Send Material</Link>
                    <Link to="/receive-material" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600 text-sm">Receive Material</Link>
                    <Link to="/quality-inspection" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600 text-sm">Quality Inspection</Link>
                    <Link to="/vendor-performance" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600 text-sm">Vendor Performance</Link>
                  </div>
                )}
              </div>
              
              {/* Mobile Masters Menu */}
              <div>
                <button 
                  onClick={() => setMobileMastersOpen(!mobileMastersOpen)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-600 flex justify-between items-center"
                >
                  Masters
                  <span>{mobileMastersOpen ? '▼' : '▶'}</span>
                </button>
                {mobileMastersOpen && (
                  <div className="ml-4 mt-1 space-y-1 text-sm">
                    <Link to="/parts-master" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600">Parts Master</Link>
                    <Link to="/customers" onClick={handleMenuClick} className="block px-3 py-2 rounded hover:bg-red-600">Customers</Link>
                  </div>
                )}
              </div>
              
              {/* Admin only mobile links */}
              {user?.role === 'admin' && (
                <>
                  <Link to="/settings" onClick={handleMenuClick} className="block px-3 py-2 rounded-lg hover:bg-red-600">⚙️ Settings</Link>
                  <Link to="/users" onClick={handleMenuClick} className="block px-3 py-2 rounded-lg hover:bg-red-600">Users</Link>
                </>
              )}
              
              <Link to="/help" onClick={handleMenuClick} className="block px-3 py-2 rounded-lg hover:bg-red-600">📖 Help</Link>
              
              {/* Mobile user info */}
              <div className="pt-2 border-t border-red-500 mt-2">
                <div className="px-3 py-2 text-sm">
                  <div className="font-semibold">{user?.full_name || 'User'}</div>
                  <div className="text-red-200 text-xs capitalize">{user?.role || 'user'}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}
